import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import { Truck, MapPin, Clock, Phone, Navigation, CheckCircle, AlertCircle } from 'lucide-react';

function VehicleTracking() {
  const { API } = useContext(AppContext);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    // Mock data
    setVehicles([
      { id: 'V-001', number: 'MH-12-AB-1234', driver: 'Raju Singh', phone: '9876543210', status: 'In Transit', orderId: 'ORD-2025-001', destination: 'Mumbai, Maharashtra', currentLocation: 'Pune Highway', eta: '2 hrs', distance: '120 km', progress: 65 },
      { id: 'V-002', number: 'DL-01-CD-5678', driver: 'Mohan Verma', phone: '9876543211', status: 'Loading', orderId: 'ORD-2025-002', destination: 'Delhi, NCR', currentLocation: 'Warehouse', eta: '-', distance: '850 km', progress: 0 },
      { id: 'V-003', number: 'TN-09-EF-9012', driver: 'Kumar Raj', phone: '9876543212', status: 'Delivered', orderId: 'ORD-2025-003', destination: 'Chennai, Tamil Nadu', currentLocation: 'Delivered', eta: '-', distance: '0 km', progress: 100 },
      { id: 'V-004', number: 'GJ-05-GH-3456', driver: 'Amit Patel', phone: '9876543213', status: 'In Transit', orderId: 'ORD-2025-004', destination: 'Ahmedabad, Gujarat', currentLocation: 'Nashik', eta: '4 hrs', distance: '280 km', progress: 35 },
      { id: 'V-005', number: 'KA-01-IJ-7890', driver: 'Suresh M', phone: '9876543214', status: 'Available', orderId: '-', destination: '-', currentLocation: 'Warehouse', eta: '-', distance: '-', progress: 0 },
    ]);
    setLoading(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'In Transit': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Loading': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'Delivered': return 'bg-green-100 text-green-700 border-green-200';
      case 'Available': return 'bg-gray-100 text-gray-700 border-gray-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'In Transit': return <Navigation className="animate-pulse" size={14} />;
      case 'Delivered': return <CheckCircle size={14} />;
      case 'Loading': return <Clock size={14} />;
      default: return <Truck size={14} />;
    }
  };

  const stats = {
    total: vehicles.length,
    inTransit: vehicles.filter(v => v.status === 'In Transit').length,
    loading: vehicles.filter(v => v.status === 'Loading').length,
    available: vehicles.filter(v => v.status === 'Available').length,
    delivered: vehicles.filter(v => v.status === 'Delivered').length,
  };

  if (loading) {
    return <div className="text-center py-12">Loading vehicle data...</div>;
  }

  return (
    <div className="space-y-6" data-testid="vehicle-tracking-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vehicle Tracking</h1>
          <p className="text-gray-500 mt-1">Real-time fleet monitoring</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <p className="text-sm text-gray-500">Total Fleet</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
          <div className="flex items-center space-x-2">
            <Navigation className="text-blue-500 animate-pulse" size={18} />
            <p className="text-sm text-blue-600 font-medium">In Transit</p>
          </div>
          <p className="text-2xl font-bold text-blue-700">{stats.inTransit}</p>
        </div>
        <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl p-5 border border-yellow-200">
          <p className="text-sm text-yellow-600 font-medium">Loading</p>
          <p className="text-2xl font-bold text-yellow-700">{stats.loading}</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
          <p className="text-sm text-green-600 font-medium">Delivered</p>
          <p className="text-2xl font-bold text-green-700">{stats.delivered}</p>
        </div>
        <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-5 border border-gray-200">
          <p className="text-sm text-gray-600 font-medium">Available</p>
          <p className="text-2xl font-bold text-gray-700">{stats.available}</p>
        </div>
      </div>

      {/* Vehicle Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {vehicles.map((vehicle) => (
          <div 
            key={vehicle.id} 
            className={`bg-white rounded-xl border p-5 hover:shadow-lg transition-all cursor-pointer ${
              selectedVehicle === vehicle.id ? 'border-[#1a237e] ring-2 ring-[#1a237e]/20' : 'border-gray-200'
            }`}
            onClick={() => setSelectedVehicle(selectedVehicle === vehicle.id ? null : vehicle.id)}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${
                  vehicle.status === 'In Transit' ? 'bg-blue-100' : 
                  vehicle.status === 'Delivered' ? 'bg-green-100' : 'bg-gray-100'
                }`}>
                  <Truck className={
                    vehicle.status === 'In Transit' ? 'text-blue-600' : 
                    vehicle.status === 'Delivered' ? 'text-green-600' : 'text-gray-600'
                  } size={24} />
                </div>
                <div>
                  <p className="font-bold text-gray-900 font-mono">{vehicle.number}</p>
                  <p className="text-sm text-gray-500">{vehicle.driver}</p>
                </div>
              </div>
              <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(vehicle.status)}`}>
                {getStatusIcon(vehicle.status)}
                <span>{vehicle.status}</span>
              </span>
            </div>

            {vehicle.status === 'In Transit' && (
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-500">Progress</span>
                  <span className="font-medium text-gray-900">{vehicle.progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-[#1a237e] to-[#3949ab] h-2 rounded-full transition-all"
                    style={{ width: `${vehicle.progress}%` }}
                  ></div>
                </div>
              </div>
            )}

            <div className="space-y-2 text-sm">
              {vehicle.orderId !== '-' && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Order</span>
                  <span className="font-mono text-[#1a237e] font-medium">{vehicle.orderId}</span>
                </div>
              )}
              {vehicle.destination !== '-' && (
                <div className="flex items-center space-x-2 text-gray-600">
                  <MapPin size={14} className="text-red-500" />
                  <span>{vehicle.destination}</span>
                </div>
              )}
              {vehicle.currentLocation && (
                <div className="flex items-center space-x-2 text-gray-600">
                  <Navigation size={14} className="text-blue-500" />
                  <span>{vehicle.currentLocation}</span>
                </div>
              )}
              {vehicle.eta !== '-' && (
                <div className="flex items-center space-x-2 text-gray-600">
                  <Clock size={14} className="text-orange-500" />
                  <span>ETA: {vehicle.eta}</span>
                </div>
              )}
            </div>

            <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
              <div className="flex items-center space-x-2 text-sm">
                <Phone size={14} className="text-gray-400" />
                <span className="text-gray-600 font-mono">{vehicle.phone}</span>
              </div>
              {vehicle.distance !== '-' && vehicle.status === 'In Transit' && (
                <span className="text-xs text-gray-500">{vehicle.distance} remaining</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default VehicleTracking;
